import { Virtuoso } from 'react-virtuoso';
import { Autocomplete, AutocompleteProps, AutocompleteRenderOptionState } from '@mui/material';
import React, { useContext, useMemo, type ReactNode } from 'react';

// We don't care (right now at least) about most of the generic arguments that MUI's Autocomplete accepts, so this
// saves us from having to repeat the type arguments multiple times below and allows us to omit the ListboxComponent
// that we'll be supplying rather than consumers supplying it
type BaseAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> = Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo, 'div'>, 'ListboxComponent'>;

export interface VirtualizedAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends BaseAutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  /**
   * A callback function for rendering an individual option in the list, identical to the MUI prop.
   */
  renderOption: NonNullable<BaseAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['renderOption']>;

  /**
   * Any valid CSS value for an element's `height` property (e.g. `100px`, `3em`, etc.)
   */
  listboxHeight?: string;

  /**
   * Any valid CSS value for an element's `height` property (e.g. `100px`, `3em`, etc.).
   * This will override the default max height of 40vh from MUI.
   */
  listboxMaxHeight?: string;
}

export const VIRTUALIZED_AUTOCOMPLETE_ESTIMATED_ROW_HEIGHT = 32;

/**
 * Meant to be a drop-in replacement for MUI's Autocomplete that automatically virtualizes the Listbox to only render
 * options that are on-screen. Requires that you supply an overall height value for the Listbox and a callback function
 * for rendering an option in the list.
 */
export function VirtualizedAutocomplete<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined = false,
>(props: VirtualizedAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>) {
  // Capture the original renderOption that was supplied so that we can propagate it via context to the
  // VirtualizedListbox to use
  const { renderOption, listboxMaxHeight, listboxHeight: propsListBoxHeight, ...others } = props;

  // Default the listboxHeight if it's not provided: Autocomplete list items are currently ~32px tall, so show a max of
  // 10 items by default taking into account if there are fewer than 10 options available overall
  const listboxHeight =
    propsListBoxHeight ?? `${Math.min(others.options.length, 10) * VIRTUALIZED_AUTOCOMPLETE_ESTIMATED_ROW_HEIGHT}px`;

  const ctxValue: VirtualizedListboxContext<T> = useMemo(
    () => ({
      listboxHeight,
      renderOption,
      listboxMaxHeight,
    }),
    [listboxHeight, listboxMaxHeight, renderOption]
  );

  return (
    // Unforunately, React context doesn't play well with generics, so here we're just casting from the <T> version to
    // the <unknown> version
    <VirtualizedListboxContext.Provider value={ctxValue as VirtualizedListboxContext}>
      <Autocomplete
        {...others}
        // This probably looks weird, but is based on MUI's example of virtualization (https://mui.com/material-ui/react-autocomplete/#virtualization)
        // The basic idea is that MUI's Autocomplete calls the `renderOption` function internally to build up the list
        // of `children` to render for the ListboxComponent. So with a `renderOption` function like this, our
        // `VirtualizedListbox` will get a `children` prop that is an array of [props, option, state] tuples which it
        // can then use as the "data" to do the actual rendering of whatever items are on-screen.
        renderOption={(props, option, state) => [props, option, state] as ReactNode}
        ListboxComponent={VirtualizedListbox}
      />
    </VirtualizedListboxContext.Provider>
  );
}

// Context propagated to the ListboxComponent by VirtualizedAutocomplete
interface VirtualizedListboxContext<T = unknown> {
  listboxHeight: string;
  renderOption: (...args: VirtualizedListboxData<T>) => React.ReactNode;
  listboxMaxHeight?: string;
}

const VirtualizedListboxContext = React.createContext<VirtualizedListboxContext | undefined>(undefined);

function useVirtualizedListboxContext() {
  const ctx = useContext(VirtualizedListboxContext);
  if (ctx === undefined) {
    throw new Error('No VirtualizedListboxContext found. Did you forget the provider?');
  }
  return ctx;
}

// This is the data that ends up getting passed down via the `children` prop by MUI Autocomplete to our
// VirtualizedListbox component
type VirtualizedListboxData<T = unknown> = [
  props: React.HTMLAttributes<HTMLLIElement>,
  option: T,
  state: AutocompleteRenderOptionState,
];

interface VirtualizedListboxProps extends Omit<React.HTMLAttributes<HTMLElement>, 'children'> {
  // MUI wants a ListboxComponent that takes ReactNode as `children`, but we know it'll actually be an array of data
  // produced by MUI calling our `renderOption` callback above, so extend the `children` type to add that
  children?: React.ReactNode | VirtualizedListboxData[];
}

// Listbox component that uses the context above to render a virtualized list using `react-virtuoso`
const VirtualizedListbox = React.forwardRef<HTMLElement, VirtualizedListboxProps>((props, ref) => {
  const { children, ...others } = props;

  // Need to cast to our array of tuples from our `renderOption` callback above
  const data = children as VirtualizedListboxData[];

  const { renderOption, listboxHeight, listboxMaxHeight } = useVirtualizedListboxContext();

  return (
    <Virtuoso
      {...others}
      data={data}
      // Increases the viewport, so that virtuoso renders a few extra items in each direction. This
      // is critical for the keyboard navigation from MUI Autocomplete to work because it tries to
      // look for the next/previous item in the DOM.
      increaseViewportBy={VIRTUALIZED_AUTOCOMPLETE_ESTIMATED_ROW_HEIGHT * 2}
      itemContent={(index, data) => {
        // Grab the arguments that MUI built up and passed via children and call the original render function
        // that the user wanted with those arguments
        const [props, option, state] = data;
        return renderOption(props, option, state);
      }}
      scrollerRef={(scrollerRef) => {
        // If no ref, nothing to send scrollerRef to
        if (ref === null) return;

        // This is kinda dumb to have to do, but this just forwards the scrollerRef if it's `HTMLElement | null` to
        // the ref provided. We need these checks to make Typescript happy because Virtuoso's scrollerRef _could_ be
        // the global Window element instead if you're using the `useWindowScroll` option (which we're not, but still
        // have to account for because of the types involved)
        if (scrollerRef === null || 'tagName' in scrollerRef) {
          if (typeof ref === 'function') {
            ref(scrollerRef);
          } else {
            ref.current = scrollerRef;
          }
        }
      }}
      style={{ height: listboxHeight, maxHeight: listboxMaxHeight }}
    />
  );
});

VirtualizedListbox.displayName = 'VirtualizedListbox';
